/* eslint-disable consistent-return */
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import {
  Button as OriginalButton,
  Icon,
  palette,
} from '@m12s/component-library';
import { ElevatedCard } from 'lib/utils/styled';

/** Light yellow bar + dark primary text (Categorize / WO id pairing). */
const MACHINE_HEADER_DARK_ON_LIGHT_VARIANTS = ['setup', 'pause'];

const machineHeader = {
  active: {
    background: palette.Green600,
    text: palette.White,
    mutedText: palette.Green300,
    shadow: true,
  },
  idle: {
    background: palette.Blue500,
    text: palette.White,
    mutedText: palette.Blue300,
    shadow: true,
  },
  unavailable: {
    background: palette.Grey600,
    text: palette.White,
    mutedText: palette.Grey400,
    shadow: true,
  },
  setup: {
    background: palette.Yellow400,
    text: palette.Grey900,
    mutedText: palette.Yellow600,
    shadow: false,
  },
  fault: {
    background: palette.Red600,
    text: palette.White,
    mutedText: palette.Red300,
    shadow: true,
  },
  rejected: {
    background: palette.Red700,
    text: palette.White,
    mutedText: palette.Red300,
    shadow: true,
  },
  pending: {
    background: palette.Orange500,
    text: palette.White,
    mutedText: palette.Orange300,
    shadow: true,
  },
  inspecting: {
    background: palette.Teal400,
    text: palette.White,
    mutedText: palette.Teal300,
    shadow: true,
  },
  pause: {
    background: palette.Yellow300,
    text: palette.Grey900,
    mutedText: palette.Grey900,
    shadow: false,
  },
};

/** Same color as the label before the Categorize button (`<Text categorize>` prefix). */
function getMachineHeaderCategorizeRowPrefixColor(variant) {
  if (variant === 'unavailable') {
    return palette.Grey500;
  }
  if (variant === 'pause') {
    return palette.Grey900;
  }
  if (
    variant !== 'setup' &&
    variant !== 'pending' &&
    variant !== 'inspecting' &&
    variant !== 'rejected'
  ) {
    return palette.Yellow400;
  }
  return machineHeader[variant].text;
}

const Text = styled.span`
  color: ${({ variant, categorize }) => {
    if (categorize) {
      return getMachineHeaderCategorizeRowPrefixColor(variant);
    }
    if (variant === 'unavailable') {
      return palette.Grey500;
    }
    if (variant === 'pause') {
      return palette.Grey900;
    }
    return machineHeader[variant].text;
  }};
  opacity: ${({ variant }) => {
    return variant === 'pause' ? 0.5 : 1;
  }};
  font-size: 1rem;
  font-weight: ${({ theme }) => {
    return theme.fontWeights.bold;
  }};
  letter-spacing: 0.005em;
  
  ${({ noLineHeight }) => {
    if (!noLineHeight) {
      return css`
        line-height: 1;
      `;
    }
  }}
  

  ${({ muted, variant }) => {
    return (
      muted &&
      variant !== 'unavailable' &&
      css`
        color: ${machineHeader[variant].mutedText};
      `
    );
  }}

  ${({ variant }) => {
    return (
      machineHeader[variant].shadow &&
      css`
        text-shadow: 1px 1px 0px rgba(0, 0, 0, 0.15);
      `
    );
  }}

  ${({ noWrap }) => {
    return (
      noWrap &&
      css`
        white-space: nowrap;
      `
    );
  }}
`;

const Header = styled.div`
  position: relative;
  z-index: 2;
  background-color: ${({ variant }) => {
    return machineHeader[variant].background;
  }};

  padding: 0.5rem;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem;

  @media (orientation: portrait) {
    padding: 0.125rem 0.25rem;
  }

  ${({ hideOnMobile }) => {
    return (
      hideOnMobile &&
      `
      @media (max-width: 576px) {
        display: none;
      }
    `
    );
  }}
`;

const Column = styled.div`
  display: flex;
  align-items: center;
  @media (orientation: portrait) {
    margin: 0.25rem;
  }

  ${({ pullRight }) => {
    return (
      pullRight &&
      `
      @media (orientation: portrait) {
        margin-left: auto;
      }
    `
    );
  }}

  ${({ block }) => {
    return (
      block &&
      `
      display: block;
      overflow: hidden;
    `
    );
  }}
`;

const MachineStatusIcon = ({ variant, icon }) => {
  return (
    <Icon
      style={{ marginLeft: '0.5rem' }}
      icon={icon}
      size="1.25rem"
      color={
        MACHINE_HEADER_DARK_ON_LIGHT_VARIANTS.includes(variant)
          ? palette.Black
          : palette.White
      }
      opacity="0.5"
    />
  );
};

MachineStatusIcon.propTypes = {
  variant: PropTypes.string.isRequired,
  icon: PropTypes.object.isRequired,
};

const JobStatusIcon = ({ variant, icon }) => {
  return (
    <Icon
      style={{ marginRight: '0.5rem' }}
      icon={icon}
      color={
        MACHINE_HEADER_DARK_ON_LIGHT_VARIANTS.includes(variant)
          ? palette.Black
          : palette.White
      }
    />
  );
};

JobStatusIcon.propTypes = {
  variant: PropTypes.string.isRequired,
  icon: PropTypes.object.isRequired,
};

const Button = styled(OriginalButton)`
  height: 1.375rem;
  font-size: 0.625rem;
  line-height: 1.25rem;
  font-weight: 700;
  letter-spacing: 0.004em;
  text-transform: uppercase;
  padding: 0 0.5rem;
  margin-left: 0.625rem;
  cursor: pointer;
  color: ${({ headerVariant }) => {
    return machineHeader[headerVariant].background;
  }};
  ${({ headerVariant }) => {
    return (
      MACHINE_HEADER_DARK_ON_LIGHT_VARIANTS.includes(headerVariant) &&
      css`
        background-color: ${palette.Black};
        &:hover {
          background-color: ${palette.Grey900};
        }
      `
    );
  }}
  ${({ headerVariant }) => {
    if (
      !headerVariant ||
      headerVariant === 'unavailable' ||
      MACHINE_HEADER_DARK_ON_LIGHT_VARIANTS.includes(headerVariant)
    ) {
      return null;
    }
    return css`
      &:hover {
        background-color: ${palette.Yellow500};
      }
    `;
  }}
`;

/** Same as `Button` (Categorize / split downtime); `margin-left: 0` when first in the job row. */
const HeaderAuxiliaryButton = styled(Button)`
  margin-left: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  position: relative;
  z-index: 1;
`;

const TouchTargetPosition = styled.span`
  height: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 640px) {
    display: none;
  }
`;

const TouchTarget = styled.a`
  width: max-content;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 2.75rem;
`;

const TextSpacer = styled.span`
  display: inline-block;
  width: ${({ width = '0.75rem' }) => {
    return width;
  }};
`;

const BottomLeftInfoGroup = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`;

const TouchAreaContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  overflow: hidden;
  gap: 0.5rem;
`;

const OperationName = styled(Text)`
  flex-direction: row;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  opacity: ${({ variant }) => {
    return variant === 'pause' ? 0.5 : 1;
  }};
`;

const ProgramName = styled(Text)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  opacity: ${({ variant }) => {
    return variant === 'pause' ? 0.5 : 1;
  }};
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.5rem;
  letter-spacing: 0.005em;
  height: 1.5rem;
  padding: 0.125rem 0;
  gap: 0.5rem;
`;

const WorkOrderInfoGroupContainer = styled.div`
  height: 1.5rem;
  font-weight: ${({ theme }) => {
    return theme.fontWeights.bold;
  }};
  padding-right: 0.7rem;
  display: flex;
  ${({ workOrderId }) => {
    return workOrderId && `min-width: 0;`;
  }}
  justify-content: flex-start;
  align-items: center;
  gap: 0.5rem;
  border-radius: 0.2rem;
  color: ${({ variant }) => {
    if (variant === 'unavailable') {
      return palette.Grey500;
    }
    return machineHeader?.[variant]?.text ?? 'inherit';
  }};
  ${({ variant }) => {
    return variant !== 'unavailable' && `cursor: pointer;`;
  }}
`;

const WorkOrderIdContainer = css`
  display: flex;
  align-items: center;
  min-width: 0;
  justify-content: center;
  height: 1.5rem;
  font-size: 1rem;
`;

const StartJobMachineHeaderButton = styled.div`
  ${WorkOrderIdContainer}
  height: 1.5rem;
  min-width: 1.5rem;
  background-color: ${palette.Yellow300};
  border-radius: 0.125rem;
  z-index: 1;

  &:hover {
    background-color: ${palette.Yellow400};
  }
  color: ${palette.Green600};
  padding: 0.125rem 0.5rem;
  gap: 0.5rem;
  cursor: pointer;
`;

const NoWorkOrderIdContainer = styled.div`
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: 1.5rem;
  min-width: 9rem;
  max-width: 100%;
  padding: 0 0.625rem;
  border-radius: 0.25rem;
  border: 1px solid transparent;
  gap: 0.375rem;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.004em;

  ${({ variant }) => {
    if (variant === 'unavailable') {
      return css`
        background-color: ${palette.Grey500};
        color: ${palette.Grey600};
      `;
    }
    if (MACHINE_HEADER_DARK_ON_LIGHT_VARIANTS.includes(variant)) {
      return css`
        background-color: rgba(0, 0, 0, 0.08);
        border-color: rgba(0, 0, 0, 0.14);
        color: ${machineHeader[variant].text};
        &:hover {
          background-color: rgba(0, 0, 0, 0.12);
        }
      `;
    }
    return css`
      background-color: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.35);
      color: ${palette.White};
      &:hover {
        background-color: rgba(255, 255, 255, 0.3);
      }
    `;
  }}
`;

const WorkOrderText = styled.div`
  margin-left: 0;
  min-width: 0;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  font-family: inherit;
  font-size: 1rem;
  font-weight: ${({ theme }) => {
    return theme.fontWeights.bold;
  }};
  line-height: 1.5rem;
  letter-spacing: 0.005em;
  opacity: ${({ isPaused }) => {
    return isPaused ? 0.5 : 1;
  }};
  color: ${({ useHeaderBG, variant, isPaused }) => {
    if (useHeaderBG) {
      return machineHeader?.[variant]?.background || 'inherit';
    }
    if (isPaused) {
      return machineHeader?.[variant]?.mutedText;
    }
    return getMachineHeaderCategorizeRowPrefixColor(variant);
  }};
`;

const MachineAndOperatorNameContainer = styled(Text)`
  cursor: default;
`;

const TouchPointArea = styled.div`
  position: fixed;
  z-index: 1;
  top: 2.4rem;
  left: 0;
  width: ${({ width }) => {
    return `${width}px`;
  }};
  height: 3.1rem;
`;

const PausedContainer = styled.div`
  margin-left: 0.75rem;
  display: flex;
  flex-direction: row;
`;

const DefaultCardBorder = ElevatedCard;

const MachineHeaderButtonText = styled.span`
  display: inline;
  color: inherit;
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-align: center;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  white-space: nowrap;

  opacity: ${({ variant }) => {
    return variant === 'pause' ? 0.5 : 1;
  }};
`;

export {
  Button,
  HeaderAuxiliaryButton,
  Column,
  Header,
  Row,
  MachineStatusIcon,
  JobStatusIcon,
  Text,
  TouchTarget,
  TouchTargetPosition,
  TextSpacer,
  BottomLeftInfoGroup,
  WorkOrderInfoGroupContainer,
  WorkOrderIdContainer,
  NoWorkOrderIdContainer,
  WorkOrderText,
  MachineAndOperatorNameContainer,
  TouchPointArea,
  TouchAreaContainer,
  OperationName,
  PausedContainer,
  DefaultCardBorder,
  MachineHeaderButtonText,
  StartJobMachineHeaderButton,
  ProgramName,
};
